import { useMemo, useState } from 'react'
import { useLanguage } from '../../context/LanguageContext'

/**
 * Multi-course Play & Stay — Rules step.
 *
 * v1 rules:
 *   - ForceOnePerPartner (boolean)
 *   - MaxRoundPrice (decimal, optional)
 *
 * v63 rules:
 *   - MinAdvanceDays (int, optional) — must book at least N days ahead
 *   - NoConsecutiveSameCourse (boolean) — can't play same course two days in a row
 *   - BlackoutDates (array of YYYY-MM-DD strings) — no bookings on these days
 *   - FreeCancellationDays (int, optional) — free cancellation up to N days before arrival
 *   - DayRestriction ('any'|'weekday'|'weekend')
 */
export default function MultiCourseRulesStep({ partners, initialRules, onBack, onNext, nextLabel, backLabel }) {
  const { t } = useLanguage()
  const [forceOnePerPartner, setForceOnePerPartner] = useState(!!initialRules?.ForceOnePerPartner)
  const [maxRoundPrice, setMaxRoundPrice] = useState(
    initialRules?.MaxRoundPrice != null ? String(initialRules.MaxRoundPrice) : ''
  )
  const [minAdvanceDays, setMinAdvanceDays] = useState(
    initialRules?.MinAdvanceDays != null ? String(initialRules.MinAdvanceDays) : ''
  )
  const [noConsecutive, setNoConsecutive] = useState(!!initialRules?.NoConsecutiveSameCourse)
  const [blackoutDates, setBlackoutDates] = useState(
    Array.isArray(initialRules?.BlackoutDates) ? initialRules.BlackoutDates : []
  )
  const [newBlackout, setNewBlackout] = useState('')
  const [freeCancellationDays, setFreeCancellationDays] = useState(
    initialRules?.FreeCancellationDays != null ? String(initialRules.FreeCancellationDays) : ''
  )
  const [dayRestriction, setDayRestriction] = useState(initialRules?.DayRestriction || 'any')

  // Partners whose base rate is above the cap — warn the creator
  const overagePartners = useMemo(() => {
    const cap = Number(maxRoundPrice)
    if (!cap || cap <= 0) return []
    return partners.filter(p => p.BaseRatePerRound != null && Number(p.BaseRatePerRound) > cap)
  }, [partners, maxRoundPrice])

  function addBlackout() {
    const d = newBlackout.trim()
    if (!d) return
    if (blackoutDates.includes(d)) { setNewBlackout(''); return }
    setBlackoutDates([...blackoutDates, d].sort())
    setNewBlackout('')
  }
  function removeBlackout(d) {
    setBlackoutDates(blackoutDates.filter(x => x !== d))
  }

  function handleNext() {
    const cap = maxRoundPrice === '' ? null : Math.max(0, Number(maxRoundPrice) || 0) || null
    const minAdv = minAdvanceDays === '' ? null : Math.max(0, Number(minAdvanceDays) || 0)
    const freeCancel = freeCancellationDays === '' ? null : Math.max(0, Number(freeCancellationDays) || 0)
    onNext({
      ForceOnePerPartner: forceOnePerPartner ? 1 : 0,
      MaxRoundPrice: cap,
      MinAdvanceDays: minAdv,
      NoConsecutiveSameCourse: noConsecutive ? 1 : 0,
      BlackoutDates: blackoutDates,
      FreeCancellationDays: freeCancel,
      DayRestriction: dayRestriction,
    })
  }

  return (
    <div className="card" style={{ padding: 28 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, color: 'var(--secondary-950)' }}>{t('sp_rules', 'Rules')}</h2>
          <p style={{ margin: '6px 0 0', color: 'var(--text-muted)', fontSize: 14 }}>
            {t('sp_rules_distribute_blurb', 'How should rounds be distributed across your')} {partners.length} {partners.length !== 1 ? t('sp_partner_courses_plural', 'partner courses') : t('sp_partner_course_singular', 'partner course')}?
          </p>
        </div>
        <button onClick={onBack} className="btn btn-outline-dark" style={{ padding: '8px 16px' }}>
          {backLabel || t('sp_back_to_partners', '← Back to partners')}
        </button>
      </div>

      {/* Partner recap chips */}
      <div style={{ padding: 16, background: '#f9fafb', borderRadius: 10, marginBottom: 20, border: '1px solid var(--card-border)' }}>
        <div style={sectionHeaderStyle}>{t('sp_selected_partners', 'Selected partners')}</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {partners.map(p => {
            const rate = p.BaseRatePerRound != null ? `$${Number(p.BaseRatePerRound).toFixed(0)}` : '—'
            const overCap = overagePartners.some(o => o.ID_NO === p.ID_NO)
            return (
              <span key={p.ID_NO} style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                fontSize: 13, fontWeight: 600, padding: '6px 12px', borderRadius: 16,
                background: overCap ? '#fef2f2' : 'var(--brand-50)',
                color: overCap ? '#991b1b' : 'var(--brand-700)',
                border: '1px solid ' + (overCap ? '#fca5a5' : 'var(--brand-500)'),
              }}>
                {overCap && <span aria-hidden="true">⚠</span>}
                {p.CourseName}
                <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>· {rate}</span>
              </span>
            )
          })}
        </div>
      </div>

      {/* Rule 1: Force one per partner */}
      <div style={{ marginBottom: 16 }}>
        <label style={{
          display: 'flex', gap: 14, alignItems: 'flex-start', padding: 16,
          border: '2px solid ' + (forceOnePerPartner ? 'var(--brand-500)' : 'var(--card-border)'),
          borderRadius: 10,
          background: forceOnePerPartner ? 'var(--brand-50)' : '#fff',
          cursor: 'pointer',
        }}>
          <Toggle
            checked={forceOnePerPartner}
            onChange={setForceOnePerPartner}
          />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--secondary-950)', marginBottom: 4 }}>
              {t('sp_require_each_partner', 'Require each partner to be played at least once')}
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 }}>
              {t('sp_force_one_when_on', 'When ON: the customer must pick a round at every partner course before repeating any. Their round count must be at least')} {partners.length} ({t('sp_number_of_partners', 'the number of partners')}).
              <br />
              {t('sp_force_one_when_off', 'When OFF: the customer can distribute rounds freely — all at one partner, or any mix they like.')}
            </div>
          </div>
        </label>
      </div>

      {/* Rule 2: Max price per round */}
      <div style={{ marginBottom: 20 }}>
        <label style={fieldLabelStyle}>{t('sp_max_round_price_label', 'Maximum price per round (optional)')}</label>
        <div style={{ position: 'relative', maxWidth: 260 }}>
          <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 16, color: 'var(--text-muted)' }}>$</span>
          <input
            type="number" min="0" step="1"
            value={maxRoundPrice}
            onChange={e => setMaxRoundPrice(e.target.value)}
            placeholder={t('sp_eg_150', 'e.g. 150')}
            style={{ ...textInputStyle, paddingLeft: 28 }}
          />
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6, lineHeight: 1.5 }}>
          {t('sp_max_round_price_hint', 'Customers will only see tee times at or below this price. Leave blank for no cap.')}
        </div>

        {overagePartners.length > 0 && (
          <div style={{
            marginTop: 12, padding: 12, borderRadius: 8,
            background: '#fef2f2', border: '1px solid #fca5a5', color: '#991b1b', fontSize: 13,
          }}>
            <strong>{t('sp_heads_up', 'Heads up:')}</strong> {overagePartners.length} {overagePartners.length !== 1 ? t('sp_partners_lower', 'partners') : t('sp_partner_lower', 'partner')} {t('sp_charge_more_than_cap', 'charge more than your cap:')}
            <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>
              {overagePartners.map(p => (
                <li key={p.ID_NO}>{p.CourseName} — ${Number(p.BaseRatePerRound).toFixed(0)}/{t('sp_round_lower', 'round')}</li>
              ))}
            </ul>
            <div style={{ marginTop: 6 }}>
              {t('sp_overage_advice', 'Customers may see few or no tee times at these courses. Consider raising the cap, or dropping these partners.')}
            </div>
          </div>
        )}
      </div>

      {/* Rule: No consecutive days at same course */}
      <div style={{ marginBottom: 16 }}>
        <label style={{
          display: 'flex', gap: 14, alignItems: 'flex-start', padding: 16,
          border: '2px solid ' + (noConsecutive ? 'var(--brand-500)' : 'var(--card-border)'),
          borderRadius: 10,
          background: noConsecutive ? 'var(--brand-50)' : '#fff',
          cursor: 'pointer',
        }}>
          <Toggle checked={noConsecutive} onChange={setNoConsecutive} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--secondary-950)', marginBottom: 4 }}>
              {t('sp_no_consecutive_label', 'No consecutive days at the same course')}
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 }}>
              {t('sp_no_consecutive_blurb', 'When ON: if the customer plays Cabot Links on Monday, they can\'t play Cabot Links on Tuesday. Keeps the trip feeling like a tour.')}
            </div>
          </div>
        </label>
      </div>

      {/* Rule: Day-of-week restriction */}
      <div style={{ marginBottom: 16 }}>
        <label style={fieldLabelStyle}>{t('sp_day_of_week_restriction', 'Day-of-week restriction')}</label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, maxWidth: 480 }}>
          {[
            { key: 'any', label: t('sp_any_day', 'Any day'), desc: t('sp_no_restriction', 'No restriction') },
            { key: 'weekday', label: t('sp_weekdays_only', 'Weekdays only'), desc: t('sp_mon_fri', 'Mon–Fri') },
            { key: 'weekend', label: t('sp_weekends_only', 'Weekends only'), desc: t('sp_sat_sun', 'Sat–Sun') },
          ].map(opt => {
            const active = dayRestriction === opt.key
            return (
              <button key={opt.key} type="button"
                onClick={() => setDayRestriction(opt.key)}
                style={{
                  padding: '12px 8px',
                  border: '2px solid ' + (active ? 'var(--brand-500)' : 'var(--card-border)'),
                  borderRadius: 8,
                  background: active ? 'var(--brand-50)' : '#fff',
                  cursor: 'pointer',
                  textAlign: 'center',
                }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: active ? 'var(--brand-700)' : 'var(--secondary-950)' }}>
                  {active ? '● ' : ''}{opt.label}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{opt.desc}</div>
              </button>
            )
          })}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6, lineHeight: 1.5 }}>
          {t('sp_dow_restriction_blurb', 'Golf days must fall on the allowed day range. Customer won\'t be able to pick a trip that starts on the wrong day.')}
        </div>
      </div>

      {/* Rule: Minimum advance booking */}
      <div style={{ marginBottom: 16 }}>
        <label style={fieldLabelStyle}>{t('sp_min_advance_booking', 'Minimum advance booking (days)')}</label>
        <input type="number" min="0" step="1"
          value={minAdvanceDays}
          onChange={e => setMinAdvanceDays(e.target.value)}
          placeholder={t('sp_eg_7', 'e.g. 7')}
          style={{ ...textInputStyle, maxWidth: 200 }} />
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6, lineHeight: 1.5 }}>
          {t('sp_min_advance_hint', 'Must be booked at least this many days before arrival. Leave blank for no minimum. Prevents last-minute bookings when partners can\'t coordinate.')}
        </div>
      </div>

      {/* Rule: Free cancellation window */}
      <div style={{ marginBottom: 16 }}>
        <label style={fieldLabelStyle}>{t('sp_free_cancel_window', 'Free cancellation window (days)')}</label>
        <input type="number" min="0" step="1"
          value={freeCancellationDays}
          onChange={e => setFreeCancellationDays(e.target.value)}
          placeholder={t('sp_eg_14', 'e.g. 14')}
          style={{ ...textInputStyle, maxWidth: 200 }} />
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6, lineHeight: 1.5 }}>
          {t('sp_free_cancel_hint', 'Customer can cancel for a full refund up to this many days before arrival. Leave blank to make the package non-refundable.')}
        </div>
      </div>

      {/* Rule: Blackout dates */}
      <div style={{ marginBottom: 20 }}>
        <label style={fieldLabelStyle}>{t('sp_blackout_dates', 'Blackout dates')}</label>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
          <input type="date"
            value={newBlackout}
            onChange={e => setNewBlackout(e.target.value)}
            style={{ ...textInputStyle, maxWidth: 200 }} />
          <button type="button" onClick={addBlackout}
            disabled={!newBlackout}
            className="btn btn-outline-dark"
            style={{ padding: '8px 14px', opacity: newBlackout ? 1 : 0.5, cursor: newBlackout ? 'pointer' : 'not-allowed' }}>
            {t('sp_plus_add_date', '+ Add date')}
          </button>
        </div>
        {blackoutDates.length > 0 ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {blackoutDates.map(d => (
              <span key={d} style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 12,
                background: '#fef2f2', color: '#991b1b', border: '1px solid #fca5a5',
              }}>
                {d}
                <button type="button" onClick={() => removeBlackout(d)}
                  style={{ background: 'none', border: 0, color: '#991b1b', cursor: 'pointer', fontSize: 14, padding: 0, lineHeight: 1 }}
                  aria-label={`${t('sp_remove', 'Remove')} ${d}`}>
                  ×
                </button>
              </span>
            ))}
          </div>
        ) : (
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{t('sp_no_blackout_dates', 'No blackout dates. Add any dates where partner courses are unavailable (events, maintenance, closed seasons).')}</div>
        )}
      </div>

      {/* Footer */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        borderTop: '1px solid var(--card-border)', paddingTop: 20, marginTop: 8,
      }}>
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          {t('sp_rules_up_next', 'Up next: package details (golfers, rounds, nights, pricing).')}
        </div>
        <button onClick={handleNext} className="btn btn-primary" style={{ padding: '12px 24px' }}>
          {nextLabel || t('sp_next_package_details', 'Next: Package details →')}
        </button>
      </div>
    </div>
  )
}

// ── Style helpers (match partners step) ──
const sectionHeaderStyle = {
  margin: '0 0 12px',
  fontSize: 13,
  fontWeight: 700,
  letterSpacing: 1,
  textTransform: 'uppercase',
  color: 'var(--text-muted)',
}
const fieldLabelStyle = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--secondary-950)',
  marginBottom: 6,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
}
const textInputStyle = {
  width: '100%',
  padding: '10px 12px',
  fontSize: 14,
  border: '1px solid var(--card-border)',
  borderRadius: 8,
  background: '#fff',
  fontFamily: 'inherit',
}

// iOS-style toggle switch. Animated knob, green when on, gray when off.
// Accessible: wraps a visually-hidden checkbox so keyboard + screen readers work.
function Toggle({ checked, onChange, disabled }) {
  return (
    <span
      role="switch"
      aria-checked={checked}
      tabIndex={disabled ? -1 : 0}
      onClick={() => !disabled && onChange(!checked)}
      onKeyDown={(e) => {
        if (disabled) return
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault()
          onChange(!checked)
        }
      }}
      style={{
        position: 'relative',
        display: 'inline-block',
        width: 44,
        height: 24,
        borderRadius: 12,
        background: checked ? 'var(--brand-500)' : '#d1d5db',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background 0.15s ease',
        flexShrink: 0,
        marginTop: 2,
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 2,
          left: checked ? 22 : 2,
          width: 20,
          height: 20,
          borderRadius: '50%',
          background: '#fff',
          boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
          transition: 'left 0.15s ease',
        }}
        aria-hidden="true"
      />
    </span>
  )
}
